/**
 * SearchInput Component
 *
 * Text input with search icon, debounced onChange, and clear button.
 * Used in both search overlay and search results page.
 */

'use client';

import { Icon } from '@/components/ui/Icon';
import { useDebouncedCallback } from '@/hooks/useDebounce';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import styles from './SearchInput.module.scss';

export interface SearchInputProps {
  /** Initial value for the input */
  defaultValue?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Called when value changes (debounced) */
  onChange?: (value: string) => void;
  /** Called when user submits (Enter key or icon click) */
  onSubmit?: (value: string) => void;
  /** Called when input is cleared */
  onClear?: () => void;
  /** Called when input receives focus */
  onFocus?: () => void;
  /** Called when input loses focus */
  onBlur?: () => void;
  /** Whether to auto-focus on mount */
  autoFocus?: boolean;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Additional class name */
  className?: string;
  /** Debounce delay in ms (default: 300) */
  debounceDelay?: number;
  /** Whether input is in overlay mode (different styling) */
  variant?: 'default' | 'overlay' | 'page';
  /** Whether to show the submit button */
  showSubmitButton?: boolean;
  /** Test ID for testing */
  'data-testid'?: string;
}

export function SearchInput({
  defaultValue = '',
  placeholder,
  onChange,
  onSubmit,
  onClear,
  onFocus,
  onBlur,
  autoFocus = false,
  size = 'md',
  className,
  debounceDelay = 300,
  variant = 'default',
  showSubmitButton = false,
  'data-testid': testId = 'search-input',
}: SearchInputProps) {
  const t = useTranslations('search');
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);

  const displayPlaceholder = placeholder || t('placeholder');

  // Update local state when defaultValue changes
  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  // Auto-focus on mount
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Debounced onChange handler
  const debouncedOnChange = useDebouncedCallback((newValue: string) => {
    onChange?.(newValue);
  }, debounceDelay);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setValue(newValue);
      debouncedOnChange(newValue);
    },
    [debouncedOnChange]
  );

  const handleClear = useCallback(() => {
    setValue('');
    onChange?.('');
    onClear?.();
    inputRef.current?.focus();
  }, [onChange, onClear]);

  const handleSubmit = useCallback(() => {
    if (value.trim()) {
      onSubmit?.(value.trim());
    }
  }, [value, onSubmit]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      } else if (e.key === 'Escape') {
        handleClear();
      }
    },
    [handleSubmit, handleClear]
  );

  return (
    <div
      className={clsx(styles.container, styles[size], styles[variant], className)}
      data-testid={testId}
    >
      {/* In overlay variant, icon is clickable to submit; otherwise decorative */}
      {variant === 'overlay' && onSubmit ? (
        <button
          type="button"
          className={clsx(styles.iconStart, styles.iconButton)}
          onClick={handleSubmit}
          aria-label={t('submit')}
        >
          <Icon name="search" size={size === 'lg' ? 24 : size === 'sm' ? 16 : 20} />
        </button>
      ) : (
        <span className={styles.iconStart} aria-hidden="true">
          <Icon name="search" size={size === 'lg' ? 24 : size === 'sm' ? 16 : 20} />
        </span>
      )}

      <input
        ref={inputRef}
        type="search"
        className={styles.input}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={displayPlaceholder}
        aria-label={t('aria.searchInput')}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
      />

      {value && (
        <button
          type="button"
          className={styles.clearButton}
          onClick={handleClear}
          aria-label={t('clear')}
        >
          <Icon name="close" size={size === 'lg' ? 20 : size === 'sm' ? 14 : 16} />
        </button>
      )}

      {showSubmitButton && (
        <button
          type="button"
          className={styles.submitButton}
          onClick={handleSubmit}
          aria-label={t('submit')}
          disabled={!value.trim()}
        >
          <Icon name="arrow-right" size={size === 'lg' ? 24 : size === 'sm' ? 16 : 20} />
        </button>
      )}
    </div>
  );
}

SearchInput.displayName = 'SearchInput';
